import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import Typography from '@mui/material/Typography';

// Icons
import PushPinIcon from '@mui/icons-material/PushPin';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import MyLocationIcon from '@mui/icons-material/MyLocation';

import ToolPanel from '../components/ToolPanel';
import TipsWindow from '../components/TipsWindow';
import { useTip } from '../hooks/useTips';
import {
  BUTTON_STYLES,
  LIST_STYLES,
  TYPOGRAPHY_STYLES,
  ROW_STYLES,
} from '../styles/viewerStyles';

function AnnotationsTools({
  annotations,
  highlightedAnnotation,
  annotationMode,
  setAnnotationMode,
  deleteAnnotation,
  clearAllAnnotations,
  exportAnnotations,
  handleAnnotationsFileImport,
  flyToAnnotation,
  highlightAnnotation,
  clearHighlight,
  toggleAnnotationsPanel,
  DRAWER_WIDTH
}) {
  const { t } = useTranslation();

  const { isOpen: isTipOpen, title: tipTitle, markdownBody: tipBody, close: closeTip } = useTip('annotations', true);

  return (
    <>
    <TipsWindow
      isOpen={isTipOpen}
      title={tipTitle}
      markdownBody={tipBody}
      onClose={({ doNotShowAgain }) => closeTip(doNotShowAgain)}
    />
    <ToolPanel
      title={t('Annotations') || 'Annotations'}
      onClose={toggleAnnotationsPanel}
      width={DRAWER_WIDTH}
    >
      {/* Action Buttons */}
      <Box sx={ROW_STYLES.buttonRow}>
        {/* Place Annotation Toggle */}
        <ToggleButton
          value="place"
          selected={annotationMode}
          onChange={() => setAnnotationMode(!annotationMode)}
          size="small"
          sx={BUTTON_STYLES.iconButton}
          title={t('PlaceAnnotation') || 'Place Annotation'}
        >
          <PushPinIcon />
        </ToggleButton>

        {/* Import Annotations */}
        <ToggleButton
          value="import"
          selected={false}
          component="label"
          size="small"
          sx={BUTTON_STYLES.iconButton}
          title={t('ImportAnnotations') || 'Import Annotations'}
        >
          <UploadIcon />
          <input
            type="file"
            accept=".json"
            onChange={handleAnnotationsFileImport}
            style={{ display: 'none' }}
          />
        </ToggleButton>

        {/* Clear All Annotations */}
        <ToggleButton
          value="clear"
          selected={false}
          onChange={clearAllAnnotations}
          size="small"
          sx={BUTTON_STYLES.deleteButton}
          title={t('ClearAllAnnotations') || 'Clear All Annotations'}
        >
          <DeleteOutlineIcon />
        </ToggleButton>
      </Box>

      {/* Annotations List */}
      {annotations.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" sx={TYPOGRAPHY_STYLES.caption}>
              {t('SavedAnnotations') || 'Saved Annotations'} ({annotations.length})
            </Typography>
            <IconButton
              onClick={exportAnnotations}
              sx={BUTTON_STYLES.smallIconButton(true)}
              title={t('ExportAnnotations') || 'Export Annotations'}
            >
              <SaveIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          </Box>

          <Box sx={LIST_STYLES.scrollContainer('400px')}>
            {annotations.map((annotation) => {
              const isHighlighted = highlightedAnnotation === annotation.id;
              return (
                <Box
                  key={annotation.id}
                  sx={LIST_STYLES.item(isHighlighted)}
                  onClick={(e) => {
                    if (e.target.closest('button')) return;
                    if (isHighlighted) {
                      clearHighlight();
                    } else {
                      highlightAnnotation(annotation.id);
                    }
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{
                      pl: 1,
                      color: isHighlighted ? '#000' : '#fff',
                      fontSize: '0.7rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block',
                      fontWeight: isHighlighted ? 'bold' : 'normal'
                    }}>
                      {annotation.text}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => flyToAnnotation(annotation)}
                      sx={BUTTON_STYLES.smallIconButton(true, isHighlighted)}
                      title={t('FlyToAnnotation') || 'Fly to Annotation'}
                    >
                      <MyLocationIcon sx={{ fontSize: '12px' }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => deleteAnnotation(annotation.id)}
                      sx={BUTTON_STYLES.smallDeleteButton(isHighlighted)}
                      title={t('DeleteAnnotation') || 'Delete Annotation'}
                    >
                      <CloseIcon sx={{ fontSize: '12px' }} />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </ToolPanel>
    </>
  );
}

AnnotationsTools.propTypes = {
  annotations: PropTypes.array.isRequired,
  highlightedAnnotation: PropTypes.string,
  annotationMode: PropTypes.bool.isRequired,
  setAnnotationMode: PropTypes.func.isRequired,
  deleteAnnotation: PropTypes.func.isRequired,
  clearAllAnnotations: PropTypes.func.isRequired,
  exportAnnotations: PropTypes.func.isRequired,
  handleAnnotationsFileImport: PropTypes.func.isRequired,
  flyToAnnotation: PropTypes.func.isRequired,
  highlightAnnotation: PropTypes.func.isRequired,
  clearHighlight: PropTypes.func.isRequired,
  toggleAnnotationsPanel: PropTypes.func.isRequired,
  DRAWER_WIDTH: PropTypes.number.isRequired
};

export default AnnotationsTools;
